import { errorResponseSchema } from '@repo/shared';
import type { z } from 'zod';

/**
 * A structured error thrown for any non-2xx API response, carrying the server's
 * machine-readable `code` so callers can branch (e.g. 401 -> logged out).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serializable request body. */
  body?: unknown;
  /** Query params appended to the URL (undefined values are skipped). */
  searchParams?: Record<string, string | number | boolean | undefined>;
}

/**
 * The client-side validation edge: every response is parsed with the shared Zod
 * schema, so the frontend fails loudly on any unexpected shape instead of
 * propagating bad data through the UI. Pass `z.void()` for 204 endpoints.
 *
 * Requests are same-origin (`/api/...`) with credentials, matching the
 * single-Worker deployment and dev proxy.
 */
export async function apiRequest<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { body, searchParams, headers, ...init } = options;

  const url = new URL(`/api${path}`, window.location.origin);
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
  }

  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...headers },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...init,
  });

  if (!response.ok) {
    throw await toApiError(response);
  }

  if (response.status === 204) {
    return schema.parse(undefined);
  }

  return schema.parse(await response.json());
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }

  const parsed = errorResponseSchema.safeParse(payload);
  if (parsed.success) {
    const { code, message, details } = parsed.data.error;
    return new ApiError(response.status, code, message, details);
  }

  return new ApiError(response.status, 'UNKNOWN', response.statusText || 'Request failed');
}
